/**
 * Citation Library API client
 * Manages saved citations in document context
 */

import { apiClient } from './client';

export interface CitationLibraryItem {
  id: string;
  document_id: string;
  user_id: string;
  title: string;
  authors: string[];
  year?: number;
  doi?: string;
  pmid?: string;
  arxiv_id?: string;
  abstract?: string;
  venue?: string;
  url?: string;
  bibtex_key?: string;
  bibtex_entry?: string;
  tags: string[];
  notes?: string;
  times_used: number;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CitationCreateRequest {
  title: string;
  authors: string[];
  year?: number;
  doi?: string;
  pmid?: string;
  arxiv_id?: string;
  abstract?: string;
  venue?: string;
  url?: string;
  bibtex_key?: string;
  bibtex_entry?: string;
  tags?: string[];
  notes?: string;
}

export interface CitationUpdateRequest {
  title?: string;
  authors?: string[];
  year?: number;
  doi?: string;
  pmid?: string;
  arxiv_id?: string;
  abstract?: string;
  venue?: string;
  url?: string;
  bibtex_key?: string;
  bibtex_entry?: string;
  tags?: string[];
  notes?: string;
}

export interface GetCitationsParams {
  search?: string;
  tags?: string;
  year_from?: number;
  year_to?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

class CitationLibraryAPI {
  private baseUrl = '/api/documents';

  /**
   * Get citations from library for a document
   */
  async getCitations(
    documentId: string,
    params?: GetCitationsParams
  ): Promise<CitationLibraryItem[]> {
    return await apiClient.get<CitationLibraryItem[]>(
      `${this.baseUrl}/${documentId}/citations`,
      params
    );
  }

  /**
   * Get a single citation by ID
   */
  async getCitation(
    documentId: string,
    citationId: string
  ): Promise<CitationLibraryItem> {
    return await apiClient.get<CitationLibraryItem>(
      `${this.baseUrl}/${documentId}/citations/${citationId}`
    );
  }

  /**
   * Save a citation to the library
   */
  async saveCitation(
    documentId: string,
    citation: CitationCreateRequest
  ): Promise<CitationLibraryItem> {
    return await apiClient.post<CitationLibraryItem>(
      `${this.baseUrl}/${documentId}/citations`,
      citation
    );
  }

  /**
   * Update a citation in the library
   */
  async updateCitation(
    documentId: string,
    citationId: string,
    updates: CitationUpdateRequest
  ): Promise<CitationLibraryItem> {
    return await apiClient.put<CitationLibraryItem>(
      `${this.baseUrl}/${documentId}/citations/${citationId}`,
      updates
    );
  }

  /**
   * Delete a citation from the library
   */
  async deleteCitation(
    documentId: string,
    citationId: string
  ): Promise<void> {
    await apiClient.delete(
      `${this.baseUrl}/${documentId}/citations/${citationId}`
    );
  }

  /**
   * Mark a citation as used (increment usage counter)
   */
  async markCitationUsed(
    documentId: string,
    citationId: string
  ): Promise<CitationLibraryItem> {
    return await apiClient.post<CitationLibraryItem>(
      `${this.baseUrl}/${documentId}/citations/${citationId}/use`
    );
  }

  /**
   * Export all citations as BibTeX
   */
  async exportBibTeX(documentId: string): Promise<{ content: string; filename: string }> {
    return await apiClient.get<{ content: string; filename: string }>(
      `${this.baseUrl}/${documentId}/citations/export/bibtex`
    );
  }
}

export const citationLibraryAPI = new CitationLibraryAPI();

